import React, { useState, useEffect } from 'react'
import { Mail, Users, TestTube, Eye, CheckCircle, AlertCircle, Clock } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { UserSelectionGrid } from './UserSelectionGrid'
import { TestEmailModal } from './TestEmailModal'

interface EmailReportsSectionProps {
  settings: {
    reportsEnabled: boolean
    sendTime: string
    weeklyReportsEnabled: boolean
    emailServiceId: string
    emailPublicKey: string
    emailTemplateId: string
  }
  onSettingsChange: (settings: any) => void
  onShowMessage: (type: 'success' | 'error' | 'info', message: string) => void
  isAccordion?: boolean
}

interface User {
  id: string
  name: string
  email?: string
  role: string
  avatar?: string
  receives_daily_reports?: boolean
  receives_weekly_reports?: boolean
}

export const EmailReportsSection: React.FC<EmailReportsSectionProps> = ({
  settings,
  onSettingsChange,
  onShowMessage,
  isAccordion = false
}) => {
  const [users, setUsers] = useState<User[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true)
      console.log('👥 Loading users from database...')

      const { data: usersData, error } = await supabase
        .from('users')
        .select('id, name, email, role, receives_daily_reports, receives_weekly_reports')
        .order('name')

      if (error) {
        console.error('❌ Error loading users:', error)
        onShowMessage('error', 'Fout bij het laden van gebruikers')
        return
      }

      if (usersData) {
        setUsers(usersData)
        console.log('✅ Users loaded:', usersData.length)
      }
    } catch (error) {
      console.error('❌ Error loading users:', error)
      onShowMessage('error', 'Fout bij het laden van gebruikers')
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const handleSettingChange = (field: string, value: any) => {
    onSettingsChange((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  const loadJsPDF = async () => {
    if ((window as any).jspdf) {
      return (window as any).jspdf.jsPDF
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
      script.onload = () => {
        setTimeout(() => {
          if ((window as any).jspdf) {
            resolve((window as any).jspdf.jsPDF)
          } else {
            reject(new Error('jsPDF not available'))
          }
        }, 100)
      }
      script.onerror = () => reject(new Error('Failed to load jsPDF'))
      document.head.appendChild(script)
    })
  }

  const generateTestReport = async () => {
    // Get today's date boundaries
    const today = new Date()
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString()
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString()

    console.log('Loading report data for today:', todayStart, 'to', todayEnd)

    // Load machines (active ones)
    const { data: machines, error: machinesError } = await supabase
      .from('machines')
      .select('*')
      .neq('status', 'inactive')
      .order('hal, name')

    if (machinesError) console.error('Error loading machines:', machinesError)

    // Load today's inspections
    const { data: inspections, error: inspectionsError } = await supabase
      .from('quality_control')
      .select('*')
      .gte('created_at', todayStart)
      .lte('created_at', todayEnd)
      .order('created_at', { ascending: false })

    if (inspectionsError) console.error('Error loading inspections:', inspectionsError)

    // Load today's problems
    const { data: problems, error: problemsError } = await supabase
      .from('probleem_meldingen')
      .select('*')
      .gte('datum_tijd', todayStart)
      .lte('datum_tijd', todayEnd)
      .order('datum_tijd', { ascending: false })

    if (problemsError) console.error('Error loading problems:', problemsError)

    // Calculate statistics
    const openProblems = (problems || []).filter((p: any) => !p.oplossing_gevonden)
    const solvedProblems = (problems || []).filter((p: any) => p.oplossing_gevonden)

    return {
      machines: machines || [],
      inspections: inspections || [],
      problems: problems || [],
      openProblems,
      solvedProblems,
      generatedAt: new Date().toISOString(),
      reportDate: today.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    }
  }

  const createPDFDocument = async (reportData: any, isTest: boolean = false) => {
    const jsPDF = await loadJsPDF()
    const doc = new jsPDF()

    const pageWidth = 210
    const margin = 20
    let yPos = 0

    // Header with KVH branding
    doc.setFillColor(146, 0, 0)
    doc.rect(0, 0, pageWidth, 35, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(22)
    doc.setFont('helvetica', 'bold')
    doc.text(isTest ? 'KVH Dagrapport (TEST)' : 'KVH Dagrapport', margin, 22)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(reportData.reportDate, margin, 30)

    yPos = 45

    // Generated timestamp
    doc.setTextColor(100, 100, 100)
    doc.setFontSize(9)
    doc.text(`Gegenereerd: ${new Date().toLocaleString('nl-NL')}`, margin, yPos)
    yPos += 15

    // Summary Section
    doc.setFillColor(245, 245, 245)
    doc.rect(margin - 5, yPos - 5, pageWidth - 2 * margin + 10, 45, 'F')

    doc.setTextColor(146, 0, 0)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Samenvatting', margin, yPos + 5)

    doc.setTextColor(0, 0, 0)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    yPos += 15
    doc.text(`Actieve machines: ${reportData.machines.length}`, margin + 5, yPos)
    yPos += 8
    doc.text(`Inspecties vandaag: ${reportData.inspections.length}`, margin + 5, yPos)
    yPos += 8
    doc.text(`Probleemmeldingen: ${reportData.problems.length} (${reportData.openProblems.length} open, ${reportData.solvedProblems.length} opgelost)`, margin + 5, yPos)
    yPos += 20

    // Inspections Section
    doc.setTextColor(146, 0, 0)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Inspecties Vandaag', margin, yPos)
    yPos += 10

    doc.setTextColor(0, 0, 0)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')

    if (reportData.inspections.length === 0) {
      doc.setTextColor(100, 100, 100)
      doc.text('Geen inspecties gevonden voor vandaag.', margin + 5, yPos)
      yPos += 10
    } else {
      reportData.inspections.slice(0, 10).forEach((inspection: any) => {
        if (yPos > 260) {
          doc.addPage()
          yPos = 20
        }
        const time = new Date(inspection.created_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
        const status = inspection.meets_requirements ? 'OK' : 'AFGEKEURD'
        doc.setTextColor(inspection.meets_requirements ? 0 : 200, inspection.meets_requirements ? 128 : 0, 0)
        doc.text(`[${status}]`, margin + 5, yPos)
        doc.setTextColor(0, 0, 0)
        doc.text(`${time} - ${inspection.employee_name || 'Onbekend'} - Product: ${inspection.product_number || '-'}`, margin + 30, yPos)
        yPos += 7
      })
      if (reportData.inspections.length > 10) {
        doc.setTextColor(100, 100, 100)
        doc.text(`... en ${reportData.inspections.length - 10} meer inspecties`, margin + 5, yPos)
        yPos += 7
      }
    }
    yPos += 10

    // Problems Section
    if (yPos > 230) {
      doc.addPage()
      yPos = 20
    }

    doc.setTextColor(146, 0, 0)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Probleemmeldingen Vandaag', margin, yPos)
    yPos += 10

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')

    if (reportData.problems.length === 0) {
      doc.setTextColor(0, 128, 0)
      doc.text('Geen problemen gemeld vandaag!', margin + 5, yPos)
    } else {
      reportData.problems.slice(0, 8).forEach((problem: any) => {
        if (yPos > 260) {
          doc.addPage()
          yPos = 20
        }
        const time = new Date(problem.datum_tijd).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
        const status = problem.oplossing_gevonden ? 'OPGELOST' : 'OPEN'
        doc.setTextColor(problem.oplossing_gevonden ? 0 : 200, problem.oplossing_gevonden ? 128 : 0, 0)
        doc.text(`[${status}]`, margin + 5, yPos)
        doc.setTextColor(0, 0, 0)
        const text = `${time} - ${problem.gebruiker_naam || 'Onbekend'} - ${(problem.argumentatie || '').substring(0, 50)}...`
        doc.text(text, margin + 35, yPos)
        yPos += 7
      })
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(150, 150, 150)
      doc.text(`Pagina ${i} van ${pageCount}`, pageWidth / 2, 290, { align: 'center' })
      doc.text('KVH Dashboard - Automatisch gegenereerd rapport', pageWidth / 2, 295, { align: 'center' })
    }

    return doc
  }

  const sendTestReport = async () => {
    try {
      onShowMessage('info', 'Test rapport wordt gegenereerd...')

      const reportData = await generateTestReport()
      const doc = await createPDFDocument(reportData, true)

      // Download the PDF
      const fileName = `KVH_Test_Rapport_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      onShowMessage('success', `PDF "${fileName}" gedownload! Check je Downloads folder.`)
    } catch (error: any) {
      console.error('Error generating test report:', error)
      onShowMessage('error', `Fout bij PDF generatie: ${error.message}`)
    }
  }

  const previewReport = async () => {
    try {
      onShowMessage('info', 'PDF wordt gegenereerd...')

      const reportData = await generateTestReport()
      const doc = await createPDFDocument(reportData, false)

      // Open PDF in new tab
      const pdfBlob = doc.output('blob')
      const pdfUrl = URL.createObjectURL(pdfBlob)
      window.open(pdfUrl, '_blank')

      onShowMessage('success', 'PDF geopend in nieuw tabblad!')
    } catch (error: any) {
      console.error('Error generating preview:', error)
      onShowMessage('error', `Fout bij PDF preview: ${error.message}`)
    }
  }


  const handleUserReportChange = (userId: string, reportType: 'daily' | 'weekly', enabled: boolean) => {
    setUsers(prev => prev.map(user => 
      user.id === userId 
        ? { 
            ...user, 
            [reportType === 'daily' ? 'receives_daily_reports' : 'receives_weekly_reports']: enabled 
          }
        : user
    ))
  }

  const handleUserEmailChange = (userId: string, email: string) => {
    setUsers(prev => prev.map(user => 
      user.id === userId ? { ...user, email } : user
    ))
  }

  const saveUserSelections = async () => {
    try {
      console.log('💾 Saving user report preferences...')
      
      // Update all users' report preferences
      for (const user of users) {
        const { error } = await supabase
          .from('users')
          .update({ 
            receives_daily_reports: user.receives_daily_reports,
            receives_weekly_reports: user.receives_weekly_reports,
            email: user.email
          })
          .eq('id', user.id)

        if (error) {
          console.error('❌ Error updating user:', user.id, error)
        }
      }

      onShowMessage('success', 'Rapport voorkeuren opgeslagen!')
    } catch (error) {
      console.error('❌ Error saving user report preferences:', error)
      onShowMessage('error', 'Fout bij het opslaan van rapport voorkeuren')
    }
  }

  const openTestEmailModal = () => {
    // Validate EmailJS config before opening modal
    if (!settings.emailServiceId || !settings.emailPublicKey || !settings.emailTemplateId) {
      onShowMessage('error', 'EmailJS configuratie is niet compleet. Configureer eerst EmailJS instellingen.')
      return
    }
    setShowTestEmailModal(true)
  }

  return (
    <div className={isAccordion ? '' : 'bg-white rounded-lg shadow-md p-6 mb-6'}>
      {!isAccordion && (
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Mail className="text-kvh-red" />
          Email Rapporten Instellingen
        </h2>
      )}
      
      {/* Enable/Disable Reports */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Automatische Rapporten</h3>
            <p className="text-gray-600">Dagelijkse PDF rapporten automatisch versturen</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.reportsEnabled}
              onChange={(e) => handleSettingChange('reportsEnabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-kvh-red/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-kvh-red"></div>
          </label>
        </div>
        
        {settings.reportsEnabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                Verzendtijd (24-uurs formaat)
              </label>
              <input
                type="time"
                value={settings.sendTime}
                onChange={(e) => handleSettingChange('sendTime', e.target.value)}
                className="w-32 p-2 border border-gray-300 rounded-md focus:ring-kvh-red focus:border-kvh-red"
              />
            </div>
          </div>
        )}
      </div>

      {/* User Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Users size={20} />
          Email Ontvangers
        </h3>
        
        {isLoadingUsers ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kvh-red mx-auto mb-2"></div>
            <p className="text-gray-500">Gebruikers laden...</p>
          </div>
        ) : (
          <>
            <UserSelectionGrid
              users={users}
              onUserReportChange={handleUserReportChange}
              onSaveSelections={saveUserSelections}
              onUserEmailChange={handleUserEmailChange}
            />
          </>
        )}
      </div>

      {/* Test Functions */}
      <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Test Functies</h3>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={openTestEmailModal}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Mail size={16} />
            Test Email Versturen
          </button>
          
          <button
            onClick={sendTestReport}
            className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors flex items-center gap-2"
          >
            <TestTube size={16} />
            Download Test PDF
          </button>
          
          <button
            onClick={previewReport}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Eye size={16} />
            PDF Voorbeeld Bekijken
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Kies naar welke admin(s) je een test email wilt versturen
        </p>
      </div>

      {/* Weekly Reports */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Weekrapporten</h3>
            <p className="text-gray-600">Elke vrijdag een samenvatting van de hele week</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.weeklyReportsEnabled}
              onChange={(e) => handleSettingChange('weeklyReportsEnabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        <p className="text-sm text-gray-500">
          Weekrapporten bevatten vergelijking met vorige week en trends
        </p>
      </div>

      {/* Test Email Modal */}
      <TestEmailModal
        isOpen={showTestEmailModal}
        onClose={() => setShowTestEmailModal(false)}
        emailConfig={{
          serviceId: settings.emailServiceId,
          publicKey: settings.emailPublicKey,
          templateId: settings.emailTemplateId
        }}
        onShowMessage={onShowMessage}
      />
    </div>
  )
}